import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, Settings, PlusCircle, UploadCloud, Building2, Flag, CreditCard, FileText, Wallet, ShoppingBag, Search, AlertTriangle, Package, HelpCircle } from 'lucide-react';
import SalesTable from '@/components/SalesTable';
import ReceiptsTable from '@/components/ReceiptsTable';
import ReportsPage from '@/components/reports/ReportsPage';
import BankReconciliationPage from '@/pages/BankReconciliationPage';
import PlatformAdjustmentsPage from '@/pages/PlatformAdjustmentsPage';
import SalesAuditPage from '@/pages/SalesAuditPage';
import SalesForm from '@/components/SalesForm';
import PasteSalesForm from '@/components/PasteSalesForm';
import ReceiptsForm from '@/components/ReceiptsForm';
import SettingsManager from '@/components/SettingsManager';
import OperatorManager from '@/components/OperatorManager';
import FlagManager from '@/components/FlagManager';
import PaymentMethodManager from '@/components/PaymentMethodManager';
import AliasManager from '@/components/AliasManager';
import SubaccountSwitcher from '@/components/SubaccountSwitcher';
import { Helmet } from 'react-helmet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSharedFilters } from '@/hooks/useSharedFilters';
import { useSalesQuota } from '@/hooks/useSalesQuota';

const DashboardPage = ({ dataManagement }) => {
  const { signOut, user, updateUserMetadata, activeAccountId } = useAuth();
  const { quota, fetchQuota, percentage, isWarning, isBlocked, planName, planNameShort } = useSalesQuota(activeAccountId);
  const { toast } = useToast();

  const reconciliationMode = user?.user_metadata?.reconciliation_mode || 'sum';

  const toggleReconciliationMode = async () => {
    try {
      const newMode = reconciliationMode === 'sum' ? 'replace' : 'sum';
      await updateUserMetadata({ reconciliation_mode: newMode });
      toast({
        title: `Modo ${newMode === 'sum' ? 'Soma' : 'Substituir'} ativado`,
        description: newMode === 'sum'
          ? "Os recebimentos serão SOMADOS ao valor atual da venda."
          : "Os recebimentos SUBSTITUIRÃO o valor atual da venda.",
      });
    } catch (error) {
      toast({
        title: "Erro ao mudar modo",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Destructure ONLY what we need. 'sales' and 'receipts' arrays are no longer used for the main tables.
  const {
    operators, flags, paymentMethods,
    loading,
    aliasModalState, closeAliasModal, resolveAliasModal,
    handleSaveSales, handleUpdateSale, handleDeleteSale, handleDeleteSalesBatch, handleDeleteAllSales,
    handleSaveReceipts, handleUpdateReceipt, handleDeleteReceipt, handleDeleteReceiptsBatch, handleDeleteAllReceipts,
    handleAddOperator, handleRemoveOperator, handleUpdateOperator,
    handleAddFlag, handleRemoveFlag,
    handleAddPaymentMethod, handleRemovePaymentMethod,
    // New Fetch Functions
    fetchSales, fetchSalesSummary,
    fetchReceipts, fetchReceiptsSummary,
    // Legacy/Utils
    forceReloadData,
    checkOrphanReceipts,
    // Gatilho de atualização instantânea
    refreshTrigger,
    handleLinkOrphanReceipts,
    handleSavePlatformAdjustments
  } = dataManagement;

  // Hook para filtros compartilhados entre as abas
  const sharedFilters = useSharedFilters();

  // Buscar cota de vendas ao montar, ao trocar de conta ou ao recarregar dados
  useEffect(() => {
    fetchQuota();
  }, [fetchQuota, refreshTrigger, activeAccountId]);

  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  const [isPasteSalesFormOpen, setIsPasteSalesFormOpen] = useState(false);
  const [isReceiptsFormOpen, setIsReceiptsFormOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isManagerOpen, setIsManagerOpen] = useState({ type: null, isOpen: false });
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isBankReconciliationOpen, setIsBankReconciliationOpen] = useState(false);
  const [isPlatformAdjustmentsOpen, setIsPlatformAdjustmentsOpen] = useState(false);
  const [isSalesAuditOpen, setIsSalesAuditOpen] = useState(false);
  const [isLinkingOrphans, setIsLinkingOrphans] = useState(false);

  const [editingSale, setEditingSale] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [orphanReceiptsModal, setOrphanReceiptsModal] = useState({ isOpen: false, data: null });
  const [selectedOrphans, setSelectedOrphans] = useState([]);
  const [pasteSalesFormState, setPasteSalesFormState] = useState({
    step: 1,
    selectedOperator: null,
    gridData: Array(20).fill(null).map((_, i) => ({
      id: Date.now() + i,
      data: '', bandeira: '', tipotransacao: '', parcelas: '', valorcompra: '', codigoautorizacao: ''
    }))
  });

  const handleOpenEditSale = useCallback(sale => {
    setEditingSale(sale);
    setIsSalesFormOpen(true);
  }, []);
  const handleCloseSalesForm = useCallback(() => {
    setEditingSale(null);
    setIsSalesFormOpen(false);
  }, []);

  const handleOpenPasteSalesForm = useCallback(() => {
    setPasteSalesFormState({
      step: 1, selectedOperator: null,
      gridData: Array(20).fill(null).map((_, i) => ({ id: Date.now() + i, data: '', bandeira: '', tipotransacao: '', parcelas: '', valorcompra: '', codigoautorizacao: '' }))
    });
    setEditingSale(null);
    setIsPasteSalesFormOpen(true);
  }, []);
  const handleClosePasteSalesForm = useCallback(() => {
    setIsPasteSalesFormOpen(false);
    setEditingSale(null);
  }, []);

  const handleCheckOrphanReceipts = useCallback(async () => {
    try {
      setSelectedOrphans([]);
      toast({ title: "Verificação iniciada", description: "Isso pode levar alguns segundos..." });
      const result = await checkOrphanReceipts();
      setOrphanReceiptsModal({ isOpen: true, data: result });
    } catch (error) {
      console.error('Erro ao verificar recebimentos orfaos:', error);
      toast({ title: "Erro", description: "Falha ao verificar órfãos.", variant: "destructive" });
    }
  }, [checkOrphanReceipts, toast]);

  const handleDeleteSelectedOrphans = useCallback(async () => {
    if (selectedOrphans.length === 0) return;
    try {
      await handleDeleteReceiptsBatch(selectedOrphans);
      toast({ title: "Sucesso", description: `${selectedOrphans.length} recebimento(s) deletado(s).` });
      setSelectedOrphans([]);
      const result = await checkOrphanReceipts();
      setOrphanReceiptsModal({ isOpen: true, data: result });
    } catch (error) {
      console.error('Erro ao deletar orfãos:', error);
      toast({ title: "Erro", description: "Falha ao deletar os registros selecionados.", variant: "destructive" });
    }
  }, [selectedOrphans, handleDeleteReceiptsBatch, checkOrphanReceipts, toast]);

  const onLinkOrphansRequested = useCallback(async () => {
    if (!orphanReceiptsModal.data?.orphanReceipts?.length) return;
    setIsLinkingOrphans(true);
    toast({ title: "Vinculando...", description: "Procurando vendas correspondentes para os órfãos listados." });

    await handleLinkOrphanReceipts(orphanReceiptsModal.data.orphanReceipts, operators, {
      onSuccess: async (linkedCount) => {
        setIsLinkingOrphans(false);
        if (linkedCount > 0) {
          // Re-executa a checagem para atualizar a tela
          const result = await checkOrphanReceipts();
          setOrphanReceiptsModal({ isOpen: true, data: result });
        }
      }
    });
  }, [orphanReceiptsModal.data, handleLinkOrphanReceipts, operators, checkOrphanReceipts, toast]);

  const handleExportOrphanReceipts = useCallback(() => {
    if (!orphanReceiptsModal.data?.orphanReceipts?.length) {
      toast({
        title: "Nenhum dado para exportar",
        description: "Nao ha recebimentos orfaos para exportar.",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text('Recebimentos Orfaos - Sistema Conciliador', 14, 22);

      // Adicionar informações do relatório
      doc.setFontSize(12);
      doc.text(`Data da analise: ${new Date().toLocaleDateString('pt-BR')}`, 14, 35);
      doc.text(`Total de recebimentos: ${orphanReceiptsModal.data.summary.totalReceipts}`, 14, 45);
      doc.text(`Recebimentos orfaos: ${orphanReceiptsModal.data.summary.orphanReceipts}`, 14, 55);
      doc.text(`Valor total orfaos: R$ ${orphanReceiptsModal.data.summary.totalOrphanValue?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 14, 65);

      // Cabeçalho da tabela
      const headers = [['Data Pag.', 'Operadora', 'Bandeira', 'Tipo', 'Valor', 'Cod. Autorizacao', 'Cod. Pedido']];

      // Dados da tabela
      const body = orphanReceiptsModal.data.orphanReceipts.map(receipt => {
        const operatorName = operators.find(op => op.id === receipt.operator_id)?.name || 'N/A';
        return [
          receipt.data_pagamento || 'N/A',
          operatorName,
          receipt.bandeira || 'N/A',
          receipt.tipo || 'N/A',
          `R$ ${parseFloat(receipt.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
          receipt.codigo_autorizacao || 'N/A',
          receipt.codigopedido || 'N/A'
        ];
      });

      // Adicionar tabela
      doc.autoTable({
        head: headers,
        body: body,
        startY: 80,
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });

      // Salvar arquivo
      const fileName = `recebimentos_orfaos_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      toast({
        title: "Exportacao PDF concluida!",
        description: `${orphanReceiptsModal.data.orphanReceipts.length} recebimentos orfaos foram exportados.`,
      });
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast({
        title: "Erro na exportacao",
        description: "Ocorreu um erro ao gerar o PDF. Tente novamente.",
        variant: "destructive"
      });
    }
  }, [orphanReceiptsModal.data, toast]);

  const handleOpenReceiptsForm = useCallback(() => { setEditingReceipt(null); setIsReceiptsFormOpen(true); }, []);
  const handleOpenEditReceipt = useCallback(receipt => { setEditingReceipt(receipt); setIsReceiptsFormOpen(true); }, []);
  const handleCloseReceiptsForm = useCallback(() => { setIsReceiptsFormOpen(false); setEditingReceipt(null); }, []);

  const openManager = useCallback((type) => setIsManagerOpen({ type, isOpen: true }), []);
  const closeManager = useCallback(() => setIsManagerOpen({ type: null, isOpen: false }), []);

  const renderManager = () => {
    switch (isManagerOpen.type) {
      case 'operators':
        return <OperatorManager operators={operators} onAddOperator={handleAddOperator} onRemoveOperator={handleRemoveOperator} onUpdateOperator={handleUpdateOperator} flags={flags} />;
      case 'flags':
        return <FlagManager flags={flags} onAddFlag={handleAddFlag} onRemoveFlag={handleRemoveFlag} />;
      case 'paymentMethods':
        return <PaymentMethodManager paymentMethods={paymentMethods} onAddPaymentMethod={handleAddPaymentMethod} onRemovePaymentMethod={handleRemovePaymentMethod} />;
      default:
        return null;
    }
  };

  const managerTitle = {
    operators: "Gerenciar Operadoras",
    flags: "Gerenciar Bandeiras",
    paymentMethods: "Gerenciar Formas de Pagamento",
    reports: "Relatórios de Vendas"
  };

  const handleSaveSale = useCallback((formData) => {
    if (editingSale) {
      handleUpdateSale({ id: editingSale.id, ...formData }, editingSale.id, { onSuccess: handleCloseSalesForm });
    }
  }, [editingSale, handleUpdateSale, handleCloseSalesForm]);

  // Se a conciliação bancária estiver aberta, mostrar apenas ela
  if (isBankReconciliationOpen) {
    return <BankReconciliationPage onClose={() => setIsBankReconciliationOpen(false)} />;
  }

  // Se as despesas IFood estiverem abertas, mostrar apenas elas
  if (isPlatformAdjustmentsOpen) {
    return <PlatformAdjustmentsPage onClose={() => setIsPlatformAdjustmentsOpen(false)} />;
  }

  // Se a auditoria de vendas estiver aberta, mostrar apenas ela
  if (isSalesAuditOpen) {
    return <SalesAuditPage onClose={() => setIsSalesAuditOpen(false)} />;
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - Conciliador de Cartões</title>
        <meta name="description" content="Visualize e gerencie suas vendas e recebimentos." />
      </Helmet>
      <div className="min-h-screen text-white flex">
        {/* Sidebar Fixa */}
        <aside className="fixed left-0 top-0 h-screen z-40 bg-[#0D1526] border-r border-white/10 flex flex-col justify-between py-4 w-[60px] hover:w-[220px] transition-all duration-300 overflow-hidden group/sidebar">
          <div>
            {/* Menu Items */}
            <nav className="flex flex-col gap-1 px-2 pt-2">
              {[
                { label: 'Operadoras', icon: Building2, onClick: () => openManager('operators') },
                { label: 'Bandeiras', icon: Flag, onClick: () => openManager('flags') },
                { label: 'Formas de Pagamento', icon: CreditCard, onClick: () => openManager('paymentMethods') },
                { label: 'Relatórios', icon: FileText, onClick: () => setIsReportsOpen(true) },
                { label: 'Conciliação Bancária', icon: Wallet, onClick: () => setIsBankReconciliationOpen(true) },
                { label: 'Despesas IFood', icon: ShoppingBag, onClick: () => setIsPlatformAdjustmentsOpen(true) },
                { label: 'Auditoria de Vendas', icon: Search, onClick: () => setIsSalesAuditOpen(true) },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors min-w-max"
                  title={item.label}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                    {item.label}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Bottom: Settings + Sair */}
          <div className="flex flex-col gap-1.5 px-2">
            {/* Widget do Plano do Usuário */}
            <div 
              className="bg-slate-900/90 border border-white/10 rounded-xl p-2 transition-all duration-300 overflow-hidden"
              title={`Plano ${planNameShort || 'Sem Plano'}${quota?.sales_limit ? ` • ${(quota.sales_count || 0).toLocaleString('pt-BR')} / ${quota.sales_limit.toLocaleString('pt-BR')} vendas (${percentage}%)` : ' • Ilimitado'}`}
            >
              <div className="flex items-center gap-3 min-w-max">
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                  isBlocked ? 'bg-red-500/20 text-red-400' : isWarning ? 'bg-amber-500/20 text-amber-400' : 'bg-sky-500/20 text-sky-400'
                }`}>
                  <Package className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-300 w-[140px]">
                  <div className="flex items-center justify-between text-xs font-semibold text-white">
                    <span className="truncate">Plano {planNameShort || 'Sem Plano'}</span>
                    {quota?.sales_limit ? (
                      <span className={`text-[10px] font-bold ${isBlocked ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-sky-400'}`}>
                        {percentage}%
                      </span>
                    ) : null}
                  </div>
                  {quota?.sales_limit ? (
                    <div className="mt-1.5 space-y-1">
                      <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            isBlocked ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' : 
                            isWarning ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]' : 
                            'bg-sky-500 shadow-[0_0_8px_rgba(14,165,233,0.6)]'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-slate-400">
                        <span>{(quota.sales_count || 0).toLocaleString('pt-BR')}</span>
                        <span>{quota.sales_limit.toLocaleString('pt-BR')}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Vendas ilimitadas
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => window.open('/ajuda', '_blank')}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-emerald-400/80 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors min-w-max"
              title="Central de Ajuda"
            >
              <HelpCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                Central de Ajuda
              </span>
            </button>
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors min-w-max"
              title="Configurações"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                Configurações
              </span>
            </button>
            <button
              onClick={signOut}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors min-w-max"
              title="Sair"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                Sair
              </span>
            </button>
          </div>
        </aside>

        {/* Conteúdo principal com offset da sidebar */}
        <div className="flex-1 ml-[60px] p-4 sm:p-6 lg:p-8">
          <header className="flex justify-between items-center mb-6">
            <img 
              src="/logotipo.png" 
              alt="Confere Cartão" 
              className="h-[35px] sm:h-[40px] w-auto object-contain"
            />
            <div className="flex items-center gap-2">
              <SubaccountSwitcher />
              
              <Button
                onClick={toggleReconciliationMode}
                variant="outline"
                size="sm"
                title={reconciliationMode === 'sum' ? "Modo Soma: Novos recebimentos serão somados ao valor atual" : "Modo Substituir: Novos recebimentos substituirão o valor atual"}
                className={`border-white/20 hover:bg-white/10 ${reconciliationMode === 'sum' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}
              >
                {reconciliationMode === 'sum' ? '➕ Modo Soma' : '🔄 Modo Substituir'}
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                size="sm"
                className="text-white border-white/20 hover:bg-white/10"
              >
                🔄 Recarregar
              </Button>
              <Button
                onClick={handleCheckOrphanReceipts}
                variant="outline"
                size="sm"
                className="text-white border-white/20 hover:bg-white/10"
              >
                🔍 Verificar Orfaos
              </Button>
            </div>
          </header>

        {/* Banner de Aviso/Bloqueio de Cota de Vendas */}
        {quota && quota.sales_limit && isBlocked && (
          <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-between text-red-200 shadow-lg shadow-red-500/5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20 text-red-400 flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-white text-sm">
                  Limite do plano atingido ({quota.sales_count?.toLocaleString('pt-BR')} / {quota.sales_limit?.toLocaleString('pt-BR')} vendas)
                </p>
                <p className="text-xs text-red-300 mt-0.5">
                  Você atingiu 100% da capacidade do seu plano ({planName}). Para lançar novas vendas, contrate um plano maior ou exclua vendas antigas para liberar espaço.
                </p>
              </div>
            </div>
          </div>
        )}

        {quota && quota.sales_limit && !isBlocked && isWarning && (
          <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between text-amber-200 shadow-lg shadow-amber-500/5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400 flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-white text-sm">
                  Atenção ao limite de vendas ({percentage}% utilizado)
                </p>
                <p className="text-xs text-amber-300 mt-0.5">
                  Você utilizou {quota.sales_count?.toLocaleString('pt-BR')} de {quota.sales_limit?.toLocaleString('pt-BR')} vendas permitidas no plano {planName}. Restam {(quota.sales_limit - quota.sales_count).toLocaleString('pt-BR')} vendas.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Removed global 'loading' check here because tables handle their own loading states */}
        <Tabs defaultValue="sales" className="w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
            <TabsList>
              <TabsTrigger value="sales">Vendas</TabsTrigger>
              <TabsTrigger value="receipts">Recebimentos</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <Button 
                onClick={isBlocked ? () => toast({ title: "🚫 Limite do Plano Atingido", description: `Limite do plano ${planName} atingido! Você possui ${quota?.sales_count?.toLocaleString('pt-BR')} de ${quota?.sales_limit?.toLocaleString('pt-BR')} vendas permitidas (0 disponíveis). Exclua vendas antigas ou contrate um plano maior para liberar novos lançamentos.`, variant: "destructive", duration: 10000 }) : handleOpenPasteSalesForm} 
                className={isBlocked ? "bg-slate-700/80 hover:bg-slate-700 text-slate-300 border border-slate-600" : "bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700"}
              >
                <UploadCloud className="w-4 h-4 mr-2" /> Lançar Vendas
              </Button>
              <Button 
                onClick={isBlocked ? () => toast({ title: "🚫 Limite do Plano Atingido", description: `Limite do plano ${planName} atingido! Você possui ${quota?.sales_count?.toLocaleString('pt-BR')} de ${quota?.sales_limit?.toLocaleString('pt-BR')} vendas permitidas (0 disponíveis). Exclua vendas antigas ou contrate um plano maior para liberar novos lançamentos.`, variant: "destructive", duration: 10000 }) : handleOpenReceiptsForm} 
                className={isBlocked ? "bg-slate-700/80 hover:bg-slate-700 text-slate-300 border border-slate-600" : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"}
              >
                <PlusCircle className="w-4 h-4 mr-2" /> Lançar Recebimentos
              </Button>
            </div>
          </div>
          <TabsContent value="sales">
            <SalesTable
              fetchSales={fetchSales}
              fetchSalesSummary={fetchSalesSummary}
              onEditSale={handleOpenEditSale}
              onDeleteSale={handleDeleteSale}
              onDeleteSalesBatch={handleDeleteSalesBatch}
              onDeleteAllSales={handleDeleteAllSales}
              operators={operators}
              flags={flags}
              paymentMethods={paymentMethods}
              sharedFilters={sharedFilters}
              refreshTrigger={refreshTrigger}
            />
          </TabsContent>
          <TabsContent value="receipts">
            <ReceiptsTable
              fetchReceipts={fetchReceipts}
              fetchReceiptsSummary={fetchReceiptsSummary}
              onDeleteReceipt={handleDeleteReceipt}
              onDeleteReceiptsBatch={handleDeleteReceiptsBatch}
              onDeleteAllReceipts={handleDeleteAllReceipts}
              onEditReceipt={handleOpenEditReceipt}
              operators={operators}
              flags={flags}
              paymentMethods={paymentMethods}
              sharedFilters={sharedFilters}
              refreshTrigger={refreshTrigger}
            />
          </TabsContent>
        </Tabs>

        <SalesForm
          isOpen={isSalesFormOpen}
          onClose={handleCloseSalesForm}
          onSave={handleSaveSale}
          operators={operators}
          flags={flags}
          paymentMethods={paymentMethods}
          sale={editingSale}
          receipts={[]} // Receipts are no longer loaded globally
        />

        <PasteSalesForm
          isOpen={isPasteSalesFormOpen}
          onClose={handleClosePasteSalesForm}
          onSave={handleSaveSales}
          operators={operators}
          flags={flags}
          paymentMethods={paymentMethods}
          formState={pasteSalesFormState}
          setFormState={setPasteSalesFormState}
          editingSale={null}
        />

        <ReceiptsForm isOpen={isReceiptsFormOpen} onClose={handleCloseReceiptsForm} onSave={handleSaveReceipts} onSavePlatformAdjustments={handleSavePlatformAdjustments} onUpdate={handleUpdateReceipt} operators={operators} flags={flags} paymentMethods={paymentMethods} receipt={editingReceipt} />

        <SettingsManager isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />

        <Dialog open={isManagerOpen.isOpen} onOpenChange={closeManager}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{managerTitle[isManagerOpen.type]}</DialogTitle>
            </DialogHeader>
            {renderManager()}
          </DialogContent>
        </Dialog>

        <Dialog open={isReportsOpen} onOpenChange={setIsReportsOpen}>
          <DialogContent className="max-w-7xl w-[90vw] h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{managerTitle.reports}</DialogTitle>
            </DialogHeader>
            <ReportsPage fetchSales={fetchSales} operators={operators} flags={flags} paymentMethods={paymentMethods} />
          </DialogContent>
        </Dialog>

        <AliasManager
          isOpen={aliasModalState.isOpen}
          alias={aliasModalState.alias}
          type={aliasModalState.type}
          options={aliasModalState.type === 'flag' ? flags : paymentMethods}
          onClose={closeAliasModal}
          onLink={resolveAliasModal}
          onAddNew={aliasModalState.type === 'flag' ? handleAddFlag : handleAddPaymentMethod}
        />

        </div> {/* End of ml-[60px] content wrapper */}
      </div> {/* End of flex container */}

      {/* Modal de Recebimentos Órfãos */}
      {orphanReceiptsModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">🔍 Analise de Recebimentos Orfaos</h2>
                <div className="flex items-center gap-2">
                  {orphanReceiptsModal.data?.orphanReceipts?.length > 0 && (
                    <>
                      <Button
                        onClick={onLinkOrphansRequested}
                        disabled={isLinkingOrphans}
                        variant="outline"
                        size="sm"
                        className="bg-green-600/30 text-green-300 border-green-500/50 hover:bg-green-600/50"
                      >
                        {isLinkingOrphans ? '🔄 Vinculando...' : '🔄 Tentar Vincular'}
                      </Button>
                      <Button
                        onClick={handleExportOrphanReceipts}
                        variant="outline"
                        size="sm"
                        className="text-white border-white/20 hover:bg-white/10"
                      >
                        📄 Exportar PDF
                      </Button>
                      {selectedOrphans.length > 0 && (
                        <Button
                          onClick={handleDeleteSelectedOrphans}
                          variant="destructive"
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white border-none"
                        >
                          🗑️ Deletar Selecionados ({selectedOrphans.length})
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    onClick={() => {
                      setSelectedOrphans([]);
                      setOrphanReceiptsModal({ isOpen: false, data: null });
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/10"
                  >
                    ✕
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {orphanReceiptsModal.data?.error ? (
                <div className="text-red-400 text-center py-8">
                  <p>Erro ao verificar recebimentos: {orphanReceiptsModal.data.error}</p>
                  <p className="text-sm mt-2">A verificação requer que os dados estejam carregados. Tente novamente após navegar pelas tabelas.</p>
                </div>
              ) : (
                <>
                  {/* Resumo */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-center">
                      <p className="text-blue-300 text-sm">Total Recebimentos</p>
                      <p className="text-2xl font-bold text-blue-400">{orphanReceiptsModal.data?.summary?.totalReceipts || 0}</p>
                    </div>
                    <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4 text-center">
                      <p className="text-green-300 text-sm">Com Venda</p>
                      <p className="text-2xl font-bold text-green-400">{orphanReceiptsModal.data?.summary?.matchedReceipts || 0}</p>
                    </div>
                    <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 text-center">
                      <p className="text-red-300 text-sm">Órfãos</p>
                      <p className="text-2xl font-bold text-red-400">{orphanReceiptsModal.data?.summary?.orphanReceipts || 0}</p>
                    </div>
                    <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 text-center">
                      <p className="text-orange-300 text-sm">Valor Órfãos</p>
                      <p className="text-2xl font-bold text-orange-400">
                        R$ {orphanReceiptsModal.data?.summary?.totalOrphanValue?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                      </p>
                    </div>
                  </div>

                  {/* Explicação */}
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
                    <h3 className="text-yellow-300 font-semibold mb-2">💡 Explicação da Divergência</h3>
                    <p className="text-white/80 text-sm">
                      A diferença entre os valores das abas Vendas e Recebimentos é causada por <strong>{orphanReceiptsModal.data?.summary?.orphanReceipts || 0} recebimentos órfãos</strong>
                      ({(orphanReceiptsModal.data?.summary?.percentage || 0)}% do total) que não possuem uma venda correspondente no sistema.
                    </p>
                  </div>

                  {/* Lista de recebimentos órfãos */}
                  {orphanReceiptsModal.data?.orphanReceipts?.length > 0 ? (
                    <div>
                      <h3 className="text-white font-semibold mb-4">📋 Recebimentos Órfãos Encontrados:</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/20">
                              <th className="text-center p-2 w-10">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-400 bg-gray-800 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  checked={selectedOrphans.length > 0 && selectedOrphans.length === orphanReceiptsModal.data.orphanReceipts.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedOrphans(orphanReceiptsModal.data.orphanReceipts.map(r => r.id));
                                    } else {
                                      setSelectedOrphans([]);
                                    }
                                  }}
                                />
                              </th>
                              <th className="text-left p-2 text-white/80">Data</th>
                              <th className="text-left p-2 text-white/80">Operadora</th>
                              <th className="text-left p-2 text-white/80">Bandeira</th>
                              <th className="text-left p-2 text-white/80">Tipo</th>
                              <th className="text-left p-2 text-white/80">Valor</th>
                              <th className="text-left p-2 text-white/80">Cód. Autorização</th>
                              <th className="text-left p-2 text-white/80">Cód. Pedido</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orphanReceiptsModal.data.orphanReceipts.map((receipt, index) => {
                              const operatorName = operators.find(op => op.id === receipt.operator_id)?.name || 'N/A';
                              return (
                                <tr key={receipt.id} className="border-b border-white/10 hover:bg-white/5">
                                  <td className="p-2 text-center">
                                    <input
                                      type="checkbox"
                                      className="rounded border-gray-400 bg-gray-800 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                      checked={selectedOrphans.includes(receipt.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedOrphans(prev => [...prev, receipt.id]);
                                        } else {
                                          setSelectedOrphans(prev => prev.filter(id => id !== receipt.id));
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className="p-2 text-white">{receipt.data_pagamento}</td>
                                  <td className="p-2 text-white">{operatorName}</td>
                                  <td className="p-2 text-white">{receipt.bandeira}</td>
                                  <td className="p-2 text-white capitalize">{receipt.tipo || 'N/A'}</td>
                                  <td className="p-2 text-white">R$ {parseFloat(receipt.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                  <td className="p-2 text-white">{receipt.codigo_autor_sanitizado || receipt.codigo_autorizacao || 'N/A'}</td>
                                  <td className="p-2 text-white">{receipt.codigopedido || 'N/A'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-green-400 text-lg">✅ Nenhum recebimento órfão encontrado!</p>
                      <p className="text-white/70 text-sm mt-2">Todos os recebimentos possuem vendas correspondentes.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default DashboardPage;

